// colorPalette.js

export function brandColors() {
  return ["#2F2F2F", "#F2F2F2", "#F5A623"]
}

/**
 * Convert a hex color string (e.g. "#RRGGBB") to an { h, s, l } object.
 * h, s, l are each in [0..1].
 */
export function hexToHSL(hex) {
  // Remove leading '#' if present
  hex = hex.replace(/^#/, "")

  // Parse the 6-character form.
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h, s
  const l = (max + min) / 2
  const d = max - min

  if (d === 0) {
    // Achromatic
    h = 0
    s = 0
  } else {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      default: // b
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  return { h, s, l }
}

/**
 * Convert HSL (each in [0..1]) to a hex color string (e.g. "#RRGGBB").
 */
export function hslToHex(h, s, l) {
  function hueToChannel(p, q, t) {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }

  let r, g, b

  if (s === 0) {
    // Achromatic
    r = g = b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hueToChannel(p, q, h + 1 / 3)
    g = hueToChannel(p, q, h)
    b = hueToChannel(p, q, h - 1 / 3)
  }

  const toHex = (x) => {
    const val = Math.round(x * 255).toString(16)
    return val.length === 1 ? "0" + val : val
  }

  return "#" + toHex(r) + toHex(g) + toHex(b)
}

/**
 * Interpolates between two HSL colors (startHSL, endHSL) given a fraction `t` in [0..1].
 */
export function interpolateHSL(startHSL, endHSL, t) {
  const h = startHSL.h + (endHSL.h - startHSL.h) * t
  const s = startHSL.s + (endHSL.s - startHSL.s) * t
  const l = startHSL.l + (endHSL.l - startHSL.l) * t
  return { h, s, l }
}

/**
 * Returns the absolute difference in lightness between two HSL colors.
 */
export function lightnessDifference(hsl1, hsl2) {
  return Math.abs(hsl1.l - hsl2.l)
}

/**
 * Check if a hex color is white or too light (should be excluded from charts)
 * @param hex - Hex color string (e.g., "#FFFFFF")
 * @returns true if the color is white or very light (lightness > 0.95)
 */
function isWhiteOrTooLight(hex) {
  const hsl = hexToHSL(hex)
  // Consider colors with lightness > 0.95 as white/too light
  // This excludes colors like #FFFFFF, #F2F2F2, etc.
  return hsl.l > 0.95
}

/**
 * Generate a gradient palette across all brand colors, returning `count` colors in total.
 *  - brandColors: array of hex strings (e.g. ["#FF0000", "#00FF00", "#0000FF"])
 *  - count: the number of colors to output in the final array
 *  - minLightnessDiff: optional minimum difference in lightness between adjacent steps (0..1)
 */
export function generateColorPalette(brandColors, count, minLightnessDiff = 0.05) {
  // Edge cases
  if (!brandColors || brandColors.length === 0) {
    return []
  }
  
  // Filter out white/too light colors from brand colors
  const filteredBrandColors = brandColors.filter(color => !isWhiteOrTooLight(color))
  
  if (filteredBrandColors.length === 0) {
    // If all brand colors were filtered out, use a default dark color
    filteredBrandColors.push("#2F2F2F")
  }
  
  if (filteredBrandColors.length === 1) {
    // With a single color, just produce variations in lightness around it
    return generateShadesOfSingleColor(filteredBrandColors[0], count, minLightnessDiff).filter(color => !isWhiteOrTooLight(color))
  }

  // Convert brand colors to HSL
  const brandHSLs = filteredBrandColors.map(hexToHSL)

  // We will have (brandHSLs.length - 1) segments
  const segments = brandHSLs.length - 1

  const result = []
  for (let i = 0; i < count; i++) {
    // t ranges from 0..1 across the entire set
    const t = i / (count - 1)

    // Which segment are we in? (Divide [0..1] into `segments` pieces)
    const segSize = 1 / segments
    const segIndex = Math.min(
      Math.floor(t / segSize),
      segments - 1 // avoid going out of range
    )

    // "Local" t for just this segment
    const tInSegment = (t - segIndex * segSize) / segSize

    // Interpolate between brandHSLs[segIndex] and brandHSLs[segIndex + 1]
    const startColor = brandHSLs[segIndex]
    const endColor = brandHSLs[segIndex + 1]
    const interpolated = interpolateHSL(startColor, endColor, tInSegment)

    // Cap lightness to avoid white colors (max 0.95)
    if (interpolated.l > 0.95) {
      interpolated.l = 0.95
    }

    // Enforce a minimum step in lightness vs. the previous color
    if (result.length > 0) {
      const prevHSL = hexToHSL(result[result.length - 1])
      if (lightnessDifference(prevHSL, interpolated) < minLightnessDiff) {
        // Slightly nudge this color's lightness (but keep it under 0.95)
        const newL = prevHSL.l + (interpolated.l > prevHSL.l ? minLightnessDiff : -minLightnessDiff)
        interpolated.l = Math.min(newL, 0.95)
      }
    }

    // Convert back to hex
    const candidateHex = hslToHex(interpolated.h, interpolated.s, interpolated.l)
    
    // Only add if not white/too light (double-check after conversion)
    if (!isWhiteOrTooLight(candidateHex)) {
      result.push(candidateHex)
    } else if (result.length > 0) {
      // If we got a white color, use a darker version of the previous color
      const prevHSL = hexToHSL(result[result.length - 1])
      const darkerHSL = { ...prevHSL, l: Math.min(prevHSL.l + minLightnessDiff, 0.9) }
      result.push(hslToHex(darkerHSL.h, darkerHSL.s, darkerHSL.l))
    } else {
      // First color is white - use the first brand color instead
      result.push(filteredBrandColors[0])
    }
  }

  // Final filter to ensure no white colors in result
  return result.filter(color => !isWhiteOrTooLight(color))
}

/**
 * If there's only one brand color, this helper function generates
 * lighter/darker variants by adjusting the lightness.
 */
export function generateShadesOfSingleColor(hexColor, count, minLightnessDiff = 0.05) {
  const base = hexToHSL(hexColor)
  const shades = []

  // We'll define a range of lightness from (base.l - 0.3) to (base.l + 0.3),
  // but cap the maximum lightness to 0.95 to avoid white colors
  const range = 0.3
  const startL = Math.max(base.l - range, 0)
  const endL = Math.min(base.l + range, 0.95) // Cap at 0.95 to avoid white

  for (let i = 0; i < count; i++) {
    const t = i / (count - 1) // from 0..1
    let newL = startL + (endL - startL) * t
    // Ensure we don't exceed the white threshold
    newL = Math.min(newL, 0.95)
    
    let shade = { h: base.h, s: base.s, l: newL }

    if (shades.length > 0) {
      const prevHSL = shades[shades.length - 1]
      if (lightnessDifference(prevHSL, shade) < minLightnessDiff) {
        shade.l = Math.min(prevHSL.l + (shade.l > prevHSL.l ? minLightnessDiff : -minLightnessDiff), 0.95)
      }
    }
    shades.push(shade)
  }

  return shades.map((hsl) => hslToHex(hsl.h, hsl.s, hsl.l))
}
